// import secureLocalStorage from 'react-secure-storage';

import { captureException } from '../../logger/Logger';
import { type IStorage, StorageRecoveryStatus } from '../types/Storage';
import { Singleton } from '../utils/Singleton';

// ENG-4788 Remove use of the react-secure-storage package. This package attempts to fingerprint the browser meaning
// that if the browser returns a different fingerprint getting a key value will return undefined. To trigger this behavior
// locally, drag a browser window from one monitor to the other assuming the monitors are not identical. This will trigger
// the ScreenPrint check to fail because window.screen returns different values.

// TO VIEW PLAIN TEXT STORAGE LOCALLY:
// 1. Comment out the line `import secureLocalStorage from 'react-secure-storage';`
// 2. Comment out the line below.
// const localStorage = secureLocalStorage;

// This storage facade provides a way to keep the interface of storage consistent
// across the app for web and native platforms, since react-native-mmkv-storage
// does not have web support. Instead, we proxy these methods to web's localStorage
// using the `react-secure-storage` library.
// Once `react-native-mmkv-storage` has web support, the facade pattern can be dropped.
class StorageFacade extends Singleton implements IStorage {
    public setBool(key: string, value: boolean) {
        this.setString(key, JSON.stringify(value));
    }

    public getBool(key: string) {
        const value = localStorage.getItem(key);

        if (typeof value === 'boolean') {
            return value;
        }

        switch (value) {
            case 'true':
                return true;
            case 'false':
                return false;
            default:
                return null;
        }
    }

    public setString(key: string, value: string) {
        try {
            localStorage.setItem(key, value);
        } catch (e: unknown) {
            captureException(e, {
                message: `Could not write key ${key} to localStorage: ${(e as DOMException).message}`,
            });
        }
    }

    public getString(key: string) {
        return localStorage.getItem(key)?.toString();
    }

    public removeItem(key: string) {
        localStorage.removeItem(key);
    }

    public clearAll() {
        localStorage.clear();
    }

    public getRecoveryStatus() {
        return StorageRecoveryStatus.NOT_NEEDED;
    }
}

export default StorageFacade.getInstance();
